import * as React from 'react';
import { observer } from 'mobx-react';
import { Modal, Select } from '@ixicare/ui';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { useDutyState } from '~hooks/useDutyState';

/** An HCP with no group receives nothing, so they're asked for one up front.
 *  Group otherwise only lives inside the duty control — this is the one case
 *  where it has to interrupt, because there's no working state to fall back to.
 *  Observers are exempt: handling no group is a legitimate state for them
 *  (view-only), which the duty control owns. */
const RequireHcpGroupModal: React.FC = observer(() => {
  const { auth } = useStores();
  const { isHcp, canSelfAssignGroup, groupOptions, activate } = useDutyState();

  // HCPs only — the old selector this came from was rendered behind `isHcp`.
  // Everyone else (installers, employees) has no business in an HCP alarm
  // group, and this Modal has no onClose, so showing it to them is a lockout.
  // Then only where the site lets HCPs pick their own group: elsewhere an admin
  // assigns it, so prompting asks for something the user can't give.
  if (!auth.user || !isHcp || !canSelfAssignGroup || auth.user.hcpGroupId || groupOptions.length === 0) {
    return null;
  }

  return (
    <Modal>
      <h3>{t('hcpGroups.currentGroup')}</h3>
      <p className="mb-20">{t('hcpGroups.modal.selectGroupPrompt')}</p>
      <div className="d-flex justify-content-end">
        <Select
          value={null}
          onChange={(groupId: string) => {
            void activate(groupId).catch((error) => console.error('Error selecting group:', error));
          }}
          options={groupOptions.map((g) => ({ value: g.id, label: g.label }))}
          placeholder={t('hcpGroups.select.placeholder')}
          as="filter"
        />
      </div>
    </Modal>
  );
});

export default RequireHcpGroupModal;
